import type { MedicalNoteType } from "../../types";
import type { DateTimeString } from "../../types/DateTimeString";
import type { Status } from "../schemas/physician";
import type { Ailment } from "../schemas/shared/Treatment";

import type { MedicalNoteAddendum } from "./MedicalNoteAddendum";
import type { PremiumType } from "./PatientProfile";
import type { UsPatientPlanOfCare } from "./UsPatientPlanOfCare";

type CommunicationGroups = "async" | "sync";
type CommunicationTypes = "none" | "phone" | "video" | "writing";
type DischargeResolutionTypes = "discharged" | "selfcare";

export const PayerTypes = {
  FROSJO: "frosjo",
  OTHER: "other",
  STOCKHOLM: "stockholm",
  VGR: "vgr",
} as const;

export type PayerType = typeof PayerTypes[keyof typeof PayerTypes];

export type Payer = {
  payer_type: PayerType;
};

export type PatientStateTypes =
  | "blocked"
  | "discharged"
  | "invited"
  | "in_diagnosis_course"
  | "in_first_health_form"
  | "in_introduction"
  | "in_limbo"
  | "in_selfcare"
  | "in_six_week_course"
  | "in_six_week_welcome"
  | "in_sustain_course"
  | "in_sustain_welcome"
  | "new"
  | "referred_elsewhere"
  | "treatment_ended";

export type PatientBillingAddress = {
  address: string;
  zip_code: string;
};

type UnregisteredActiveField = {
  confirmed_date?: boolean;
};

export type CareEvent = {
  ailment?: Ailment;
  billable?: boolean;
  cap35?: boolean;
  condition?: string | null;
  confirmed_call?: boolean;
  confirmed_identity?: boolean;
  confirmed_insurance?: boolean;
  confirmed_video_call?: boolean;
  confirmed_chaperone?: boolean; // Used in UK market
  confirmed_insurance_and_identity?: boolean;
  confirmed_understanding?: boolean;
  comment?: string | null;
  communication_group: CommunicationGroups | null;
  communication_type: CommunicationTypes | null;
  cpt_codes?: string[];
  created_at: DateTimeString | null;
  discharge_resolution?: DischargeResolutionTypes | null;
  exclusion_reason?: string | null;
  frequency?: string | null;
  general_condition: string | null;
  handled_at?: DateTimeString | null;
  health_care_professional_name: string;
  health_care_professional_type: string;
  icd_code?: string | null;
  icd_codes?: string[];
  id: number;
  label: MedicalNoteType;
  medical_note_addenda?: MedicalNoteAddendum[];
  medical_record_note: string | null;
  message_interaction_id?: number;
  npi?: string | null;
  pain_location?: string | null;
  patient_billing_address?: PatientBillingAddress | null;
  patient_birthday?: string | null;
  patient_email?: string | null;
  patient_id: number;
  patient_deleted: boolean;
  patient_state?: PatientStateTypes;
  patient_name: string;
  patient_phone_number?: string | null;
  patient_preferred_name: string;
  payer_id?: number;
  physician_name?: string | null;
  problem: string;
  rtm_care_event_log_id?: number;
  signed_at: DateTimeString | null;
  signed_limit_reached?: boolean;
  start_time: DateTimeString | null;
  structured_fields: CareEventStructuredFields;
  suggested?: boolean;
  therapist_assignment_role?: string | null;
  treatment_duration?: number | null;
  treatment_frequency?: string | null;
  user_course_week_id?: number;
  us_patient_plan_of_care?: UsPatientPlanOfCare | null;
  premium_type: PremiumType;
  week_number?: number;
  payer?: Payer;
  status?: Status;
  medical_referral_id?: number | null; // From admin billing view
  plan_of_care_id?: number | null; // From admin billing view
} & UnregisteredActiveField;

export type CareEventStructuredFields = {
  complexity?: string | null;
  red_flags?: string | null;
  subjective?: string | null;
  objective?: string | null;
  assessment?: string | null;
  function?: string | null;
  past_medical_history?: string | null;
  previous_and_current_treatments?: string | null;
  treatment?: string | null;
  chaperone_name?: string | null; // Used in UK market
  discharge_patient_f2f?: boolean | null; // Used in UK market
  discharge_self_management?: boolean | null; // Used in UK market
  discharge_gp_action?: boolean | null; // Used in UK market
  discharge_advanced_physio?: boolean | null; // Used in UK market
  discharge_physio_f2f?: boolean | null; // Used in UK market
  discharge_other_text?: string | null; // Used in UK market
  current_pain?: number | null;
  current_functionality?: number | null;
  goal_pain?: number | null;
  goal_pain_achieved?: string | null;
  goal_functionality?: number | null;
  goal_functionality_achieved?: string | null;
  goal_free_text?: string | null;
  goal_free_text_achieved?: string | null;
  plan_duration_days?: number | null;
  adherence_measure?: string | null;
  improvement_pain?: string | null;
  improvement_functionality?: string | null;
  progress_towards_goal?: string | null;
  skilled_care_provided?: string | null;
  clinical_note?: string | null;
  measure?: string | null;
  frequency?: string | null;
  symptoms_and_pain?: string | null;
  social?: string | null;
  aids?: string | null;
  physical_activity?: string | null;
  previous_surveys?: string | null;
  plan?: string | null;
  results?: string | null;
  treatment_period?: string | null;
  exclusion_reason?: string | null;
  suggested_assessment_classification?: string | null;
  suggested_assessment_recommendation?: string | null;
  suggested_measure_classification?: string | null;
  suggested_measure_recommendation?: string | null;
  suggested_plan_recommendation?: string | null;
  bmi?: number | null;
  kva_codes?: string[] | null;
};
